import React, { Component, ErrorInfo, ReactNode } from 'react';
import { reportError } from '../utils/errorReporting';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error details
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Update state with error info
        this.setState({
            error,
            errorInfo
        });

        // Report error to centralized error reporting system
        reportError(error, errorInfo);

        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            // Render custom fallback UI or default error UI
            return this.props.fallback || <ErrorFallback error={this.state.error} />;
        }

        return this.props.children;
    }
}

// Default error fallback component
interface ErrorFallbackProps {
    error?: Error;
    resetError?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => {
    const handleReset = () => {
        if (resetError) {
            resetError();
        } else {
            // Default reset behavior - reload the page
            window.location.reload();
        }
    };

    return (
        <div className="bg-red-50 border border-red-200 rounded p-4 m-2">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <svg
                        className="h-5 w-5 text-red-500 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                    </svg>
                </div>
                <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">
                        Component Error
                    </h3>
                    <p className="mt-1 text-sm text-red-700">
                        Something went wrong with this component. Please try refreshing the page.
                    </p>

                    {process.env.NODE_ENV === 'development' && error && (
                        <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-red-600 hover:text-red-800">
                                Error Details (Development)
                            </summary>
                            <pre className="mt-1 text-xs text-red-600 bg-red-100 p-2 rounded overflow-auto max-h-20">
                                {error.message}
                                {error.stack && `\n\n${error.stack}`}
                            </pre>
                        </details>
                    )}

                    <div className="mt-3 flex gap-2">
                        <button
                            onClick={handleReset}
                            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                        >
                            Try Again
                        </button>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="text-xs bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 transition-colors"
                        >
                            Go Home
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ErrorBoundary;
